/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import * as localForage from 'localforage';

import { GardenHudSystem } from './GardenHudSystem';
import { MultiplayerStateComponent } from '../../components/MultiplayerComponents';
import { PlotIdentityComponent } from '../../components/PlotIdentityComponents';
import { System } from 'ecsy';
import { getOnlyEntity } from '../../utils/entityUtils';

const IDENTITY_STORAGE_KEY = 'gxs_plot_identity';
// Seconds to wait after the last edit before persisting (debounce).
const SAVE_DELAY = 1.5;
const MAX_NAME_LENGTH = 24;

/**
 * Owns the player's plot personalization (garden name + accent colour): creates
 * the singleton PlotIdentityComponent, loads/saves it to localForage, feeds it
 * to the flatscreen HUD (name label + accent theme), and mirrors the chosen
 * name onto the multiplayer identity so it shows on the leaderboard.
 *
 * The HUD DOM lives on {@link GardenHudSystem}'s overlay; this system reaches it
 * through that system rather than owning any DOM itself.
 */
export class PlotIdentitySystem extends System {
	init() {
		this._saveTimer = 0;
		this._appliedHash = '';

		const entity = this.world.createEntity();
		entity.addComponent(PlotIdentityComponent);
		this._identityEntity = entity;

		this._loadIdentity();
	}

	_identity() {
		return this._identityEntity.getMutableComponent(PlotIdentityComponent);
	}

	async _loadIdentity() {
		let saved = null;
		try {
			saved = await localForage.getItem(IDENTITY_STORAGE_KEY);
		} catch (e) {
			console.warn('Failed to load plot identity', e);
		}
		const identity = this._identity();
		if (saved) {
			if (typeof saved.gardenName === 'string') {
				identity.gardenName = saved.gardenName.slice(0, MAX_NAME_LENGTH);
			}
			if (typeof saved.accentColor === 'string') {
				identity.accentColor = saved.accentColor;
			}
		}
		identity.loaded = true;
		// Force a re-apply on the next frame now that stored values are in.
		this._appliedHash = '';
	}

	_saveIdentity() {
		const identity = this._identity();
		localForage
			.setItem(IDENTITY_STORAGE_KEY, {
				gardenName: identity.gardenName,
				accentColor: identity.accentColor,
			})
			.catch((e) => console.warn('Failed to save plot identity', e));
	}

	/** The GardenHudOverlay instance, or null if the HUD system isn't up yet. */
	_overlay() {
		const hudSystem = this.world.getSystem(GardenHudSystem);
		return hudSystem ? hudSystem._overlay : null;
	}

	execute(delta) {
		const overlay = this._overlay();
		if (!overlay) return;

		// Wire the overlay's personalization callback once the overlay exists.
		if (!overlay.onSavePersonalization) {
			overlay.onSavePersonalization = (gardenName, accentColor) => {
				const identity = this._identity();
				identity.gardenName = (gardenName || '')
					.trim()
					.slice(0, MAX_NAME_LENGTH);
				identity.accentColor = accentColor || '';
				identity.dirty = true;
				this._saveTimer = 0;
			};
		}

		const identity = this._identity();

		// Push the current identity into the HUD + multiplayer whenever it changes.
		const hash = `${identity.gardenName}|${identity.accentColor}`;
		if (hash !== this._appliedHash) {
			this._appliedHash = hash;
			if (overlay.setIdentity) {
				overlay.setIdentity({
					gardenName: identity.gardenName,
					accentColor: identity.accentColor,
				});
			}
			this._syncMultiplayerName(identity.gardenName);
		}

		// Debounced persistence after an edit.
		if (identity.dirty) {
			this._saveTimer += delta;
			if (this._saveTimer >= SAVE_DELAY) {
				identity.dirty = false;
				this._saveTimer = 0;
				this._saveIdentity();
			}
		}
	}

	/**
	 * Reflect the chosen garden name onto the multiplayer identity so the
	 * leaderboard's "You:" line and future presence use it. This updates the
	 * local component; the server was already joined with the auto-generated
	 * name, so full server-side propagation is left to the gifting/visiting pass.
	 */
	_syncMultiplayerName(gardenName) {
		if (!gardenName) return;
		const mpEntity = getOnlyEntity(this.queries.multiplayer, false);
		if (!mpEntity) return;
		const mp = mpEntity.getMutableComponent(MultiplayerStateComponent);
		if (mp) mp.localPlayerName = gardenName;
	}
}

PlotIdentitySystem.queries = {
	multiplayer: { components: [MultiplayerStateComponent] },
};
